import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Settings2, Loader2 } from 'lucide-react';
import { ColumnMapping } from '@/lib/csvParser';

interface ColumnMappingDialogProps {
  headers: string[];
  currentMapping: ColumnMapping;
  onConfirm: (mapping: ColumnMapping) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ColumnMappingDialog({
  headers,
  currentMapping,
  onConfirm,
  onCancel,
  isLoading,
}: ColumnMappingDialogProps) {
  const { t } = useTranslation(['clients', 'common']);
  const [mapping, setMapping] = useState<ColumnMapping>(currentMapping);

  const handleChange = (field: keyof ColumnMapping, value: string) => {
    setMapping(prev => ({
      ...prev,
      [field]: value === '__none__' ? undefined : value,
    }));
  };

  const isValid = mapping.email && (mapping.firstName || mapping.lastName);

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(mapping);
    }
  };

  const fieldLabels: Record<keyof ColumnMapping, string> = {
    firstName: t('firstName'),
    lastName: t('lastName'),
    email: t('email'),
    phone: t('phone'),
    emergencyContact: t('emergencyContact'),
    subscriptionType: t('import.mapping.subscriptionType'),
    subscriptionDate: t('import.mapping.subscriptionDate'),
    paymentStatus: t('import.mapping.paymentStatus'),
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          {t('import.mapping.title')}
        </CardTitle>
        <CardDescription>{t('import.mapping.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Required fields */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">
              {t('import.mapping.required')}
            </h4>
            
            <MappingField
              field="email"
              label={fieldLabels.email}
              value={mapping.email}
              headers={headers}
              onChange={handleChange}
              required
            />
            
            <MappingField
              field="firstName"
              label={fieldLabels.firstName}
              value={mapping.firstName}
              headers={headers}
              onChange={handleChange}
              required
            />
            
            <MappingField
              field="lastName"
              label={fieldLabels.lastName}
              value={mapping.lastName}
              headers={headers}
              onChange={handleChange}
            />
          </div>

          {/* Optional fields */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">
              {t('import.mapping.optional')}
            </h4>
            
            <MappingField
              field="phone"
              label={fieldLabels.phone}
              value={mapping.phone}
              headers={headers}
              onChange={handleChange}
            />
            
            <MappingField
              field="emergencyContact"
              label={fieldLabels.emergencyContact}
              value={mapping.emergencyContact}
              headers={headers}
              onChange={handleChange}
            />

            <MappingField
              field="subscriptionType"
              label={fieldLabels.subscriptionType}
              value={mapping.subscriptionType}
              headers={headers}
              onChange={handleChange}
            />

            <MappingField
              field="subscriptionDate"
              label={fieldLabels.subscriptionDate}
              value={mapping.subscriptionDate}
              headers={headers}
              onChange={handleChange}
            />

            <MappingField
              field="paymentStatus"
              label={fieldLabels.paymentStatus}
              value={mapping.paymentStatus}
              headers={headers}
              onChange={handleChange}
            />
          </div>
        </div>

        {!isValid && (
          <p className="text-sm text-destructive">
            {t('import.mapping.requiredHint')}
          </p>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid || isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('import.mapping.confirm')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MappingField({
  field,
  label,
  value,
  headers,
  onChange,
  required,
}: {
  field: keyof ColumnMapping;
  label: string;
  value?: string;
  headers: string[];
  onChange: (field: keyof ColumnMapping, value: string) => void;
  required?: boolean;
}) {
  const { t } = useTranslation(['clients']);
  
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Select value={value || '__none__'} onValueChange={(v) => onChange(field, v)}>
        <SelectTrigger>
          <SelectValue placeholder={t('import.mapping.selectColumn')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">
            <span className="text-muted-foreground">{t('import.mapping.noColumn')}</span>
          </SelectItem>
          {headers.map((header) => (
            <SelectItem key={header} value={header}>
              {header}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
